import { Box, Flex } from '@lego/klik-ui';
import { Navigate, useParams } from 'react-router-dom';
import { ApplicationVersionContent } from '@/components/application_version/ApplicationVersionContent';
import { ApplicationVersionDetails } from '@/components/application_version/ApplicationVersionDetails';
import { Loader } from '@/components/common';
import { BreadCrumb } from '@/components/common/BreadCrumb';
import { useApplicationVersion } from '@/hooks/application_version';
import { useDocumentTitle } from '@/hooks/common';
import { useRegisterPageContext } from '@/providers/PageContextProvider';
import { paths } from '@/utility/global_paths';

const ApplicationVersionPageContent = ({
  workspaceId,
  applicationId,
  applicationVersionId,
}: {
  workspaceId: number;
  applicationId: number;
  applicationVersionId: number;
}) => {
  const { data, error, isLoading } = useApplicationVersion(
    workspaceId,
    applicationId,
    applicationVersionId
  );
  useRegisterPageContext(data);
  useDocumentTitle(`Application Version #${applicationVersionId}`);

  if (isLoading || error || !data) {
    return (
      <Box>
        <Loader />
      </Box>
    );
  }

  const applicationVersion = data;

  return (
    <Box>
      <Box>
        <Box>
          <BreadCrumb
            crumbs={[
              { label: 'Workspaces', path: paths.workspaces },
              {
                label: `Workspace #${workspaceId}`,
                path: paths.workspaceById(workspaceId),
              },
              {
                label: `Application #${applicationId}`,
                path: paths.applicationById(workspaceId, applicationId),
              },
              {
                label: `Application Version #${applicationVersionId}`,
                path: paths.applicationVersionById(
                  workspaceId,
                  applicationId,
                  applicationVersionId
                ),
              },
            ]}
          />
        </Box>
        <Flex direction="column" gap={8} marginY={8}>
          <ApplicationVersionDetails applicationVersion={applicationVersion} />
          <ApplicationVersionContent applicationVersion={applicationVersion} />
        </Flex>
      </Box>
    </Box>
  );
};

export const ApplicationVersionPage = () => {
  const { workspaceId, applicationId, applicationVersionId } = useParams();

  if (
    !workspaceId ||
    !Number.parseInt(workspaceId, 10) ||
    !applicationId ||
    !Number.parseInt(applicationId, 10) ||
    !applicationVersionId ||
    !Number.parseInt(applicationVersionId, 10)
  ) {
    return <Navigate to={paths.notFound} />;
  }

  return (
    <ApplicationVersionPageContent
      applicationId={Number.parseInt(applicationId, 10)}
      applicationVersionId={Number.parseInt(applicationVersionId, 10)}
      workspaceId={Number.parseInt(workspaceId, 10)}
    />
  );
};
